// ⚠️ Editing this file? Read docs/legal-version-bump.md first.
// Substantive changes require bumping CURRENT_LEGAL_VERSION in
// src/shared/legal-version.js — otherwise users won't be re-prompted.

export default function OriPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#f9f5e9] text-ink">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
        <a
          href="/"
          className="mb-8 inline-block text-sm font-medium text-ink/70 hover:text-ink"
        >
          ← Back to Ori
        </a>

        <header className="mb-10">
          <h1
            className="mb-2 text-2xl font-bold uppercase tracking-tight"
            style={{
              fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
              color: '#3A331D',
            }}
          >
            Oriscen Privacy Policy
          </h1>
          <p className="text-sm text-ink/60">Effective: 2026</p>
        </header>

        <Prose>
          <p>
            Oriscen, Inc. (“Oriscen,” “Ori,” “we,” “us,” “our,”) provides an artificial
            intelligence-powered social agent that operates via your phone’s native messaging
            apps to help you plan and organize hangouts with friends and family.
          </p>
          <p>
            This Privacy Policy explains how we collect, use and share the personal information
            that we may collect, when you visit the subdomains and websites on{' '}
            <a href="https://www.oriscen.com/" target="_blank" rel="noopener noreferrer">
              https://www.oriscen.com/
            </a>{' '}
            (the “Sites”), become a Customer, contact us to obtain more information, send an
            opt-in text or SMS message to our platform, or otherwise directly through your use of
            our online platform and services (“Services”). A reference to “Oriscen,” “Ori,” “we,”
            “us,” or the “Company” is a reference to Oriscen and its relevant affiliates involved
            in the collection, use, sharing, or other processing of personal information.
          </p>
          <p>Please visit the following links to learn more about our Privacy Policy:</p>
          <ol>
            <li>Whose Personal Information We Collect</li>
            <li>Personal Information We Collect</li>
            <li>How We Use Your Personal Information</li>
            <li>How We Share Your Personal Information</li>
            <li>Cookies or Similar Technologies</li>
            <li>Privacy Rights &amp; Additional Disclosures for Certain U.S. Residents</li>
            <li>Children’s Privacy</li>
            <li>International Transfers of Data</li>
            <li>Change of Control</li>
            <li>Data Security and Retention</li>
            <li>Privacy Policy Updates</li>
            <li>Contact Information</li>
          </ol>

          <Section title="1. Whose Personal Information We Collect">
            <p>
              Oriscen obtains information from various entities. These entities are defined below
              by what they are referred to in this Privacy Policy and when information is
              collected. Entities may overlap and are not exclusive categories.
            </p>
            <ul>
              <li>
                Website visitors: When you visit our Sites or contact us directly for more
                information.
              </li>
              <li>
                Customers: When you contact us or engage with our Services, including free and
                paid versions.
              </li>
              <li>
                Non-Customers: When a Customer asks to help plan, coordinate, or organize
                activities through the Services with friends, family members, or other contacts,
                who are not Customers with Ori, we may be authorized by the Customer to process
                personal information about those aforementioned individuals.
              </li>
            </ul>
            <p>
              <strong>Third Party Links and Websites.</strong> Our Sites and Services may contain
              links to and from websites of third parties. If you follow a link to any of these
              websites, please note that these websites have their own privacy policies and that
              we do not accept any responsibility or liability for these policies. Please check
              these polices before you submit any data to these websites.
            </p>
          </Section>

          <Section title="2. Personal Information We Collect">
            <p>
              <strong>Information You Give Us.</strong> You may give us information by signing up
              for an online account, entering information through our forms or surveys, inputting
              information while using our Services (including sharing information about yourself
              or other individuals via messages), or contacting us by phone or email for
              information or customer service.
            </p>
            <p>The categories of information include:</p>
            <ul>
              <li>
                Identifiers, such as real name, alias, postal address, unique personal
                identifier, online identifier, Internet Protocol (“IP”) address, email address,
                account name, or other similar identifiers.
              </li>
              <li>
                Categories of personal information described in Section 1798.80(e) of the
                California Civil Code, such as name, address, or telephone number.
              </li>
              <li>
                Commercial information, such as products or services purchased, obtained or
                considered, or other purchasing or consumer histories or tendencies.
              </li>
              <li>
                Internet or other electronic network activity information, such as browsing
                history, search history, and information regarding a consumer’s interaction with
                an Internet website, application or advertisement.
              </li>
              <li>Geolocation data.</li>
              <li>Electronic or similar information.</li>
              <li>
                Inferences drawn from your personal information to create a profile about you and
                your preferences, characteristics, psychological trends, predispositions,
                behavior, attitudes, intelligence, abilities, and aptitudes.
              </li>
            </ul>
            <p>The categories of sensitive personal information include:</p>
            <ul>
              <li>
                Contents of mail, email, and text messages you send to Ori or otherwise authorize
                us to process;
              </li>
              <li>
                Precise location information, if you choose to provide it or authorize us to
                access it; and
              </li>
              <li>
                Other sensitive information that you voluntarily include in your messages or
                requests. This covers all statutorily required categories of sensitive personal
                information under California law.
              </li>
            </ul>
            <p>
              When you input information while using our Services, including but not limited to
              sending messages, granting us access to third-party services, or instructing the
              Services to retrieve or act on information you provide, you acknowledge that you are
              responsible for ensuring you have the necessary rights, permissions, or authority
              to do so. We collect each category of sensitive personal information solely for the
              purposes outlined in Section 3.
            </p>

            <h3>Information We Automatically Collect.</h3>
            <ul>
              <li>
                Sites: Like many website operators, we collect information that
                your browser sends whenever you visit our Sites. This includes log data, such as
                your computer’s IP address, browser type, browser version, the pages of our Site
                that you visit, the time and date of your visit, the time spent on those pages and
                other statistics, and whether you reached our page via a social or email campaign.
              </li>
              <li>
                Services: Some of our Services will collect information such as IP addresses,
                log-in credentials, connectivity details and device and browser information,
                approximate or precise location information if enabled, usage logs, interaction
                timestamps, error reports, and information about how you interact with the
                Services. This is used for access control and management.
              </li>
            </ul>

            <p>
              <strong>Information We Collect from Third Parties.</strong> If you access our Sites
              or Services through third parties (e.g., Meta or Google), or if you share content
              from our Sites to a third-party social media service, the third-party service will
              send us information about you if the third-party service and your account settings
              allow such sharing. The information we receive will depend on the policies and your
              account settings with the third-party service.
            </p>
            <p>
              <strong>Google Maps Platform.</strong> Our Services use Google Maps Platform to
              provide place information, imagery, and reviews. Google may collect and process data
              in connection with these features as described in the{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google Privacy Policy
              </a>
              , which is incorporated herein by reference.
            </p>
          </Section>

          <Section title="3. How We Use Your Personal Information">
            <p>We use your personal information for the following purposes:</p>
            <ul>
              <li>
                To provide you with the Services on our Sites, which includes:
                <ul>
                  <li>maintaining and servicing accounts,</li>
                  <li>providing customer service,</li>
                  <li>processing and filling orders and transactions,</li>
                  <li>verifying customer information,</li>
                  <li>processing payments,</li>
                  <li>providing advertising or marketing services to you.</li>
                  <li>understanding your messages, prompts, instructions, and context;</li>
                  <li>
                    generating responses, suggestions, reminders, plans, or other AI-powered
                    outputs.
                  </li>
                </ul>
              </li>
              <li>
                To provide analytics services, such as analyzing customer usage and improving the
                Sites and Services offered.
              </li>
              <li>
                To evaluate and improve the safety, reliability, and performance of our Services.
                We may use aggregated, deidentified, or otherwise privacy-protective information
                to improve the Services, including training and improving our AI models.
              </li>
              <li>To conduct market research and project planning.</li>
              <li>
                To detect security incidents, protect against fraudulent or other criminal
                activity, debug and repair errors, and maintain the overall quality and safety of
                our Sites and Services.
              </li>
              <li>
                To share your website visitor activity, through website cookies, with third-party
                marketing partners to market our Services and our Sites and other websites that
                you may visit.
              </li>
              <li>To fulfill our legal and financial obligations.</li>
              <li>To investigate and resolve disputes.</li>
            </ul>
            <p>
              <strong>Marketing.</strong> We may use your personal information to contact you with
              newsletters, marketing, or promotional materials and other information that may be
              of interest to you. You may opt out of receiving any, or all, of the communications
              from us by following the unsubscribe instructions provided in any email we send, or
              you can contact us using the contact details provided at the bottom of this Privacy
              Policy. You will still continue to receive service-related messages concerning
              products and Services you have purchased (unless we have indicated otherwise). For
              more information about our mobile messaging services, please visit our{' '}
              <a href="/terms">Mobile Terms</a>.
            </p>
          </Section>

          <Section title="4. How We Share Your Personal Information">
            <p>
              <strong>To Users Or the Public With Your Consent.</strong> We may share your
              information with other users or the public with your consent. The settings you
              choose will determine how much information is shared with other users and/or the
              public. For example, you may choose to leave feedback or a testimonial, and you may
              choose to share that feedback or testimonial with other users and/or the public. If
              you ask the Services to coordinate with other individuals, we may share the
              information necessary to carry out your request, such as proposed plans,
              availability, event details, or messages you authorize us to share.
            </p>
            <p>
              <strong>Affiliates and Service Providers.</strong> We share your information with
              our third-party service providers and any subcontractors as required to offer our
              Services. The service providers we use help us to:
            </p>
            <ul>
              <li>
                Run, operate, and maintain our Sites and Services through third-party platforms
                and software tools;
              </li>
              <li>Perform content moderation and crash analytics;</li>
              <li>Run email and mobile messaging campaigns;</li>
              <li>
                Provide measurement services and targeted ads (you can opt out of these services
                at websites such as{' '}
                <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer">
                  http://www.aboutads.info/choices
                </a>
                );
              </li>
              <li>Provide payment attribution; and</li>
              <li>Provide technical and customer support.</li>
            </ul>
            <p>
              Some external service providers may also collect information directly from you (for
              example, a payment processor may request your billing and financial information) in
              accordance with their own privacy policy. These external service providers do not
              share your financial information, like credit card number, with us, but may share
              limited information with us related to your purchase, like your zip code.
            </p>
            <p>
              <strong>Aggregated or Deidentified Data.</strong> We may use and disclose aggregated
              or otherwise deidentified information that does not identify and cannot reasonably be
              linked to an individual. We will maintain and use deidentified information in
              deidentified form and will not attempt to reidentify it, except as permitted or
              required by applicable law.
            </p>
            <p>
              <strong>Third Party Marketing and Advertising Cookies.</strong> With respect to
              website cookies, we share your information with third-party marketing and
              advertising partners to market our products and Services on our Sites and other
              websites you may visit.
            </p>
            <p>
              <strong>Third Party Marketing.</strong> If you are one of our Customers, California
              Civil Code Section 1798.83 permits you to request information regarding the
              disclosure of your personal information to third parties for third parties’ direct
              marketing purposes. To make such a request, please contact us using the details in
              the “Contact Information” section below.
            </p>
            <p>
              <strong>Disclosures Required by Law.</strong> We may be required to disclose your
              data in response to lawful requests by public authorities, including meeting law
              enforcement requirements. We may be under a duty to disclose or share your personal
              information to comply with any legal obligation, to enforce or apply our terms and
              conditions and other agreements, to protect our rights, property, safety, or to
              protect the rights, property, or safety of others. This includes exchanging
              information with other companies and organizations for the purposes of fraud
              protection.
            </p>
          </Section>

          <Section title="5. Cookies or Similar Technologies">
            <p>
              To make our Sites and Services work properly, we use technologies such as cookies,
              web beacons, clear GIFs, canvas fingerprinting, and other means, such as Google
              Remarketing or Facebook Pixel to collect information.
            </p>

            <h3>Cookies</h3>
            <p>
              We sometimes place small data files called cookies on your device. A cookie is a
              small text file that a website saves on your computer or mobile device when you
              visit the site. It enables the website to remember your actions and preferences
              (such as login, shopping cart, or other preferences) over a period of time, so you
              do not have to keep re-entering them whenever you come back to the site or browse
              from one page to another.
            </p>
            <p>
              You may wish to restrict the use of cookies. Please be aware that some of the
              features of our Sites may not function correctly if you disable cookies. Most modern
              browsers allow you to change your cookie settings. You can usually find these
              settings in the options or preferences menu of your browser. To understand these
              settings, the following links for commonly used browsers may be helpful:
            </p>
            <ul>
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie settings in Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie settings in Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie settings in Microsoft Edge
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie settings in Safari web
                </a>{' '}
                and{' '}
                <a
                  href="https://support.apple.com/en-us/HT201265"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  iOS
                </a>
              </li>
            </ul>

            <h3>Do Not Track</h3>
            <p>
              Some browsers provide you with a way to signal that you do not want your browsing
              activity to be tracked. The Sites may not currently respond to all Do Not Track
              (“DNT”) or similar signals, as we are awaiting consensus from the Internet policy
              and legal community on the meaning of DNT and the best way to respond to these
              signals.
            </p>
            <p>
              California and certain other U.S. state residents may opt out of tracking
              technologies by broadcasting an Opt-Out Preference Signal, such as the Global
              Privacy Control (“GPC”) (on the browsers and/or browser extensions that support such
              a signal). To download and use a browser supporting the GPC browser signal, click
              here:{' '}
              <a href="https://globalprivacycontrol.org/orgs" target="_blank" rel="noopener noreferrer">
                https://globalprivacycontrol.org/orgs
              </a>
              . If you choose to use the GPC signal, you will need to turn it on for each supported
              browser or browser extension you use. Your request to opt out of sale/sharing will
              be linked to your browser identifier only and not linked to any account information
              because the connection between your browser and the account is not known to us.
            </p>
          </Section>

          <Section title="6. Privacy Rights & Additional Disclosures for Certain U.S. Residents">
            <p>
              Several jurisdictions grant state residents certain rights and disclosures. We
              provide the following information to further help you understand your potential
              privacy rights. If you would like to exercise any rights or inquire as to whether
              such rights are available to you, please email us at{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>.
            </p>
            <p>
              <strong>Request for Information.</strong> Residents of certain U.S. states have the
              right to know whether we are processing your personal information, and in some
              instances, you have the right to request that we disclose to you the categories
              listed below for the preceding 12 months. In responding to this right, we shall
              provide to you:
            </p>
            <ul>
              <li>The categories of personal information we collect about you.</li>
              <li>The categories of sources from which your personal information is collected.</li>
              <li>
                The business or commercial purpose(s) for collecting, selling, sharing, or
                disclosing your personal information, and the categories of personal information
                disclosed for such purpose(s).
              </li>
              <li>The categories of third parties with whom we share your personal information.</li>
              <li>
                The categories of personal information we have sold, if any, about you and the
                categories of third parties to whom your personal information was sold, by category
                or categories of personal information for each third party to whom the personal
                information was sold.
              </li>
              <li>The specific pieces of personal information we have collected about you.</li>
            </ul>
            <p>
              <strong>Request for Deletion.</strong> Residents of certain U.S. states have the
              right to request we delete your personal information. Once we receive your request
              and confirm your identity, we will delete or deidentify personal information and
              direct our service providers to take similar action.
            </p>
            <p>
              <strong>Request for Correction.</strong> Residents of certain U.S. states have the
              right to request the correction or rectification of inaccurate information in the
              resident’s personal information.
            </p>
            <p>
              <strong>Request for Portable Data.</strong> Residents of certain U.S. states have
              the right to receive, in certain circumstances, a portable format of their personal
              information that allows the data to be transmitted to another entity.
            </p>
            <p>
              <strong>Do Not Sell or Share My Personal Information.</strong> Residents of certain
              U.S. states have the right to opt out of the sale or sharing of the consumer’s
              personal information. You may opt out of such sale or sharing by clicking “Do Not
              Sell or Share My Personal Information” link on the Sites or the Services or contact
              us using the details in the “Contact Information” section below.
            </p>
            <p>
              <strong>Limiting the Use of Sensitive Personal Information.</strong> Residents of
              California have the right to direct us to use or disclose sensitive personal
              information only for providing goods or services, or as otherwise minimally permitted
              under applicable law.
            </p>
            <p>
              However, we do not use or disclose sensitive personal information for the purpose of
              inferring characteristics about you. We use or disclose sensitive personal
              information only as reasonably necessary to provide the Services, maintain security,
              comply with law, or as otherwise permitted by applicable law. You retain the
              statutory right to limit our use of your sensitive personal information.
            </p>
            <p>
              <strong>Automated Processing and Profiling.</strong> Residents of certain U.S. states
              have the right to opt out of the processing of personal information for purposes of
              profiling in furtherance of decisions that produce legal or similarly significant
              effects concerning a consumer. However, we do not process your personal information
              for purposes of profiling in furtherance of decisions that produce legal or similarly
              significant effects.
            </p>
            <p>
              <strong>Right to Equal Service and Price.</strong> You have the right to receive
              equal service and price, even if you exercise a privacy right.
            </p>
            <p>
              <strong>Response Timing and Fees.</strong> We respond to verifiable consumer
              requests within 45 days, extendable another 45 days with prior notice. Two verifiable
              right to know requests per 12-month period are provided free of charge.
            </p>
            <p>
              <strong>Verification Process for Exercising Rights.</strong> To protect our
              consumers’ privacy, we verify privacy rights requests to ensure that only you (or
              your authorized agent) can exercise rights pertaining to your personal information
              with reasonable methods. As part of our verification process, we may request you to
              submit additional information.
            </p>
            <p>
              If you are using an authorized agent wishing to exercise rights on behalf of a state
              resident, please contact us using the information at the bottom of this Privacy
              Policy and provide us with a copy of the consumer’s written authorization designating
              you as their agent. We may need to verify your identity and place of residence before
              completing your rights request. We will not require you to provide more information
              than reasonably necessary to verify your identity or authority to make the request.
            </p>
            <p>
              <strong>Appeal Process.</strong> In certain cases, we may decline to take action on
              your request as permitted by law. Should we decline your request, we may provide you
              with a written explanation of the reasons for our decision and an opportunity to
              appeal the decision.
            </p>
            <p>
              If we deny your appeal, we may provide a written explanation of our decision with
              instructions on how to contact your local Attorney General or Department of Justice
              for further review, where permitted under certain U.S. state privacy laws.
            </p>
          </Section>

          <Section title="7. Children’s Privacy">
            <p>
              The Sites and Services are not directed at children under the age of 18. We do not
              knowingly collect personal information from children under 18. If you become aware
              that a child has provided us with personal information, please contact us using the
              contact details provided at the end of this Privacy Policy, and we may delete or
              anonymize associated personal information in accordance with applicable law within a
              reasonable time.
            </p>
          </Section>

          <Section title="8. International Transfers of Data">
            <p>
              Any information that you provide to us may be accessed, shared, or processed by our
              offices, located in the United States, and service providers located in the United
              States, if such data transfer is necessary for the specific purpose for which you
              submitted your data (such as the provision of goods or services under a written
              contract). This may entail a transfer of your personal information across
              international borders. The data protection standards may differ and be lower than the
              standards enforced in your jurisdiction. We maintain appropriate safeguards as
              required by applicable law for any personal information transferred internationally.
            </p>
          </Section>

          <Section title="9. Change of Control">
            <p>
              Personal information may be transferred to a third party because of a sale,
              acquisition, merger, reorganization, or other change in control. If we sell, merge,
              or transfer any part of the business, part of the sale may include your personal
              information.
            </p>
          </Section>

          <Section title="10. Data Security and Retention">
            <p>
              We implement a variety of security measures to maintain the safety of your personal
              information when you enter, submit, or access your personal information. However, no
              website, application, or transmission can guarantee security. Thus, while we have
              established and maintain what we believe to be appropriate technical and
              organizational measures to protect the confidentiality, security, and integrity of
              personal information obtained through the Sites, we cannot ensure or warrant the
              security of any information you transmit to us.
            </p>
            <p>
              We retain information from or about you for so long as necessary to fulfill the
              purposes outlined in this Privacy Policy. When the information is no longer necessary
              for these purposes, we delete it or keep it in a form that does not identify you,
              unless we are required by law to keep this information for a longer period. When
              determining the retention period, we take into account various criteria, such as the
              type of products and services requested by or provided to you, the nature and length
              of our relationship with you, possible re-enrollment with our products or services,
              the impact on the services we provide to you if we delete some information from or
              about you, mandatory retention periods provided by law and the statute of
              limitations.
            </p>
          </Section>

          <Section title="11. Privacy Policy Updates">
            <p>
              This Privacy Policy may be periodically updated. When we make material changes, we
              may provide reasonable notice if required by applicable law. We encourage you to
              periodically review this Privacy Policy to ensure you are familiar with the most
              current version. Please review carefully before submitting personal information to
              our Sites or Services. The date this Privacy Policy was last updated is indicated at
              the top of the page. We will review and update this Privacy Policy at least once every
              12 months where required by applicable law.
            </p>
          </Section>

          <Section title="12. Contact Information">
            <p>
              If you wish to contact us or have any questions or complaints about this Privacy
              Policy, please contact us using the following information:
            </p>
            <p>
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>
            </p>
            <p>
              2261 Market Street STE 86857
              <br />
              San Francisco, CA 94114
            </p>
          </Section>
        </Prose>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2
        className="mb-4 text-lg font-semibold"
        style={{
          fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
          color: '#3A331D',
        }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      style={{ fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif' }}
    >
      {children}
    </div>
  );
}
